import type { GetServerSidePropsContext } from "next";
import type { QueryClient } from "@tanstack/react-query";
import { getServerSideRequestParams } from "./getServerSideRequestParams";
import type { ServerSideRequestParams } from "./getServerSideRequestParams";

export type RequestContext = {
  requestParams?: ServerSideRequestParams;
  queryClient?: QueryClient;
  serverSide?: boolean;
};

let requestContextStore: {
  run: <T>(store: RequestContext, callback: () => T) => T;
  getStore: () => RequestContext | undefined;
} | null = null;

if (typeof window === "undefined") {
  const { AsyncLocalStorage } = require("async_hooks");
  requestContextStore = new AsyncLocalStorage();
}

export const getRequestContext = (): RequestContext => {
  if (typeof window !== "undefined" || !requestContextStore) return {};
  return requestContextStore.getStore() ?? {};
};

export const setRequestContext = (key: keyof RequestContext, value: unknown): void => {
  if (typeof window !== "undefined" || !requestContextStore) return;
  const store = requestContextStore.getStore();
  if (store) store[key] = value as never;
};

export const getRequestContextStore = () => requestContextStore;

/** Run SSR code with Tourismo API request headers (origin/referer). */
export const runWithServerSideRequestContext = async <T>(
  context: GetServerSidePropsContext,
  fn: () => Promise<T>,
): Promise<T> => {
  const store = getRequestContextStore();
  const requestParams = await getServerSideRequestParams(context);
  if (store) {
    return store.run({ requestParams, serverSide: true }, fn);
  }
  return fn();
};
